import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  ServiceAdjustmentForm,
  ServiceAdjustmentFormJunction,
} from "../types/serviceAdjustmentForms";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import {
  loopLog,
  identityChanges,
  useValueChurnProbe,
} from "../utils/loopDebug";
import { useRef } from "react";
import { useApp } from "./AppContext";
import { useSalonScopedFetch } from "../hooks/useSalonScopedFetch";

interface ServiceAdjustmentFormsContextType {
  adjustmentForms: ServiceAdjustmentForm[];
  formJunctions: ServiceAdjustmentFormJunction[];
  isLoading: boolean;
  error: string | null;
  fetchAdjustmentForms: () => Promise<void>;
  fetchFormJunctions: (
    formId?: string,
  ) => Promise<ServiceAdjustmentFormJunction[]>;
  upsertAdjustmentForm: (
    formData: ServiceAdjustmentForm,
  ) => Promise<ServiceAdjustmentForm>;
  deleteAdjustmentForm: (formId: string) => Promise<void>;
  connectFormToServices: (
    formId: string,
    serviceIds: string[],
  ) => Promise<ServiceAdjustmentFormJunction[]>;
  disconnectFormFromService: (junctionId: string) => Promise<void>;
}

const ServiceAdjustmentFormsContext =
  createContext<ServiceAdjustmentFormsContextType | null>(null);

export function ServiceAdjustmentFormsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [adjustmentForms, setAdjustmentForms] = useState<
    ServiceAdjustmentForm[]
  >([]);
  const [formJunctions, setFormJunctions] = useState<
    ServiceAdjustmentFormJunction[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businessInfo } = useSalon() as any;
  const { fetchWithAuth } = useApp();

  const fetchAdjustmentForms = useCallback(async () => {
    if (!businessInfo?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORMS, "", {
          salonId: businessInfo.id,
        }),
      );
      setAdjustmentForms(response || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch adjustment forms",
      );
      console.error("Error fetching adjustment forms:", err);
      loopLog("adjustment-forms fetch FAILED", String(err));
    } finally {
      setIsLoading(false);
    }
  }, [businessInfo?.id, fetchWithAuth]);

  const fetchFormJunctions = useCallback(
    async (formId?: string): Promise<ServiceAdjustmentFormJunction[]> => {
      if (!businessInfo?.id) return [];

      try {
        const response = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORM_JUNCTIONS,
            "",
            {
              salonId: businessInfo.id,
            },
          ),
        );

        if (formId) {
          // Return ONLY this form's junctions. The junctions endpoint takes no
          // form filter and returns every junction for the salon, so we must
          // filter client-side by form id — otherwise callers (e.g. the edit
          // wizard) are seeded with the union of every form's services, which
          // both mis-displays the selection and makes shared-service edits a
          // no-op / delete the wrong form's junction.
          return (response || []).filter(
            (junction: ServiceAdjustmentFormJunction) =>
              junction.service_adjustment_form_id === formId,
          );
        } else {
          // If fetching all junctions, update state
          setFormJunctions(response || []);
          return response || [];
        }
      } catch (err) {
        console.error("Error fetching form junctions:", err);
        loopLog("form-junctions fetch FAILED", String(err));
        return [];
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  const upsertAdjustmentForm = useCallback(
    async (formData: ServiceAdjustmentForm): Promise<ServiceAdjustmentForm> => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        // Sanitize form data: replace empty strings with null for numeric fields
        // and remove temporary IDs (those starting with "temp-")
        const sanitizedFormData = {
          ...formData,
          questions: formData.questions.map((question) => {
            const { question_id, ...questionRest } = question;
            const sanitizedQuestion = {
              ...questionRest,
              // Only include question_id if it's not temporary
              ...(question_id && !question_id.startsWith("temp-")
                ? { question_id }
                : {}),
              options: question.options.map((option) => {
                const { option_id, ...optionRest } = option;
                return {
                  ...optionRest,
                  // Only include option_id if it's not temporary
                  ...(option_id && !option_id.startsWith("temp-")
                    ? { option_id }
                    : {}),
                  duration_delta_mins:
                    option.duration_delta_mins === null ||
                    (option.duration_delta_mins as any) === "" ||
                    option.duration_delta_mins === undefined
                      ? null
                      : option.duration_delta_mins,
                  price_delta:
                    option.price_delta === null ||
                    (option.price_delta as any) === "" ||
                    option.price_delta === undefined
                      ? null
                      : option.price_delta,
                };
              }),
            };
            return sanitizedQuestion;
          }),
        };

        const response = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORMS, "", {
            salonId: businessInfo.id,
          }),
          {
            method: "POST",
            body: JSON.stringify(sanitizedFormData),
          },
        );

        // Refetch all forms to get the updated list
        await fetchAdjustmentForms();
        return response;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to save adjustment form",
        );
        console.error("Error saving adjustment form:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth, fetchAdjustmentForms],
  );

  const deleteAdjustmentForm = useCallback(
    async (formId: string) => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORM_BY_ID, "", {
            salonId: businessInfo.id,
            formId,
          }),
          {
            method: "DELETE",
          },
        );

        setAdjustmentForms((prev) =>
          prev.filter((form) => form.service_adjustment_form_id !== formId),
        );
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to delete adjustment form",
        );
        console.error("Error deleting adjustment form:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  const connectFormToServices = useCallback(
    async (
      formId: string,
      serviceIds: string[],
    ): Promise<ServiceAdjustmentFormJunction[]> => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        // Parallelize all connection requests
        const connectionPromises = serviceIds.map((serviceId) =>
          fetchWithAuth(
            getApiUrl(
              API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORM_JUNCTIONS,
              "",
              {
                salonId: businessInfo.id,
              },
            ),
            {
              method: "POST",
              body: JSON.stringify({
                service_adjustment_form_id: formId,
                salon_service_id: serviceId,
              }),
            },
          ),
        );

        const results = await Promise.all(connectionPromises);

        // Update local state with new junctions
        setFormJunctions((prev) => [...prev, ...results]);
        return results;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to connect form to services",
        );
        console.error("Error connecting form to services:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  const disconnectFormFromService = useCallback(
    async (junctionId: string) => {
      if (!businessInfo?.id) {
        throw new Error("No salon ID available");
      }

      try {
        setIsLoading(true);
        setError(null);

        await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_ADJUSTMENT_FORM_JUNCTION_BY_ID,
            "",
            {
              salonId: businessInfo.id,
              junctionId,
            },
          ),
          {
            method: "DELETE",
          },
        );

        setFormJunctions((prev) =>
          prev.filter(
            (junction) =>
              junction.service_adjustment_form_junction_id !== junctionId,
          ),
        );
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to disconnect form from service",
        );
        console.error("Error disconnecting form from service:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [businessInfo?.id, fetchWithAuth],
  );

  // [LOOP] instrumentation: record WHEN the old effect's dep identities change
  // (they drove the infinite loop — fetchWithAuth churned identity on every
  // AppProvider re-render). With the stable-fetchWithAuth fix these should
  // only ever change on a real salon switch; log if they don't stay quiet.
  const prevDepsRef = useRef<Record<string, unknown>>({});
  useEffect(() => {
    const deps = {
      businessInfoId: businessInfo?.id,
      fetchAdjustmentForms,
      fetchFormJunctions,
    };
    const changed = identityChanges(prevDepsRef.current, deps);
    if (changed.length > 0) {
      loopLog("service-adjustment dep identity changed", {
        businessInfoId: businessInfo?.id,
        changedDeps: changed,
      });
    }
    prevDepsRef.current = deps;
  });

  // Fetch adjustment forms ONCE per salon (multi-location loop fix): a failed
  // fetch (e.g. backend 403 on a second location) must NOT refire on identity
  // churn — only on salon switch or explicit refetch via the exposed
  // fetchAdjustmentForms/fetchFormJunctions. Errors land in `error` state.
  useSalonScopedFetch(
    businessInfo?.id,
    () => Promise.all([fetchAdjustmentForms(), fetchFormJunctions()]),
    { tag: "service-adjustment-forms" },
  );

  // [LOOP] churn probe
  useValueChurnProbe("ServiceAdjustmentForms", {
    adjustmentForms,
    formJunctions,
    isLoading,
    error,
  });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: ServiceAdjustmentFormsContextType = useMemo(
    () => ({
      adjustmentForms,
      formJunctions,
      isLoading,
      error,
      fetchAdjustmentForms,
      fetchFormJunctions,
      upsertAdjustmentForm,
      deleteAdjustmentForm,
      connectFormToServices,
      disconnectFormFromService,
    }),
    [
      adjustmentForms,
      formJunctions,
      isLoading,
      error,
      fetchAdjustmentForms,
      fetchFormJunctions,
      upsertAdjustmentForm,
      deleteAdjustmentForm,
      connectFormToServices,
      disconnectFormFromService,
    ],
  );

  return (
    <ServiceAdjustmentFormsContext.Provider value={value}>
      {children}
    </ServiceAdjustmentFormsContext.Provider>
  );
}

export function useServiceAdjustmentForms() {
  const context = useContext(ServiceAdjustmentFormsContext);
  if (!context) {
    throw new Error(
      "useServiceAdjustmentForms must be used within a ServiceAdjustmentFormsProvider",
    );
  }
  return context;
}
